import * as React from 'react'
import {FC, useEffect, useState} from 'react'
import {observer} from 'mobx-react'
import cn from 'classnames'
import {Transition} from 'react-transition-group'
import {useStores} from '../../lib/MobXProvider'
import {WidgetApi} from '../../lib/WidgetApi'
import {buildSlideTransitionProps} from '../../lib/helpers'
import {IconInfo} from './Icons'

///////////////////////////////////////////////////////////////////////////////

type InfoToggleProps = {
  idKey: string
  title?: string
  textHTML?: string
  uiTransitionTimeout?: number
  className?: string
  expanded?: boolean
}

///////////////////////////////////////////////////////////////////////////////

export const InfoToggle: FC<InfoToggleProps> = observer((props) => {
  const {api} = useStores<{api: WidgetApi}>()
  const {idKey, title, textHTML, className, expanded: initialExpanded} = props
  const mainId = api.ui.generateUniqueElementId(idKey)
  const isExpandable = !!textHTML
  const HeaderInnerTag = (isExpandable ? 'button' : 'div') as any
  const HeaderInnerTitleTag = (isExpandable ? 'span' : 'p') as any
  let {uiTransitionTimeout: timeout} = props

  const [expanded, setExpanded] = useState(!!initialExpanded)

  useEffect(() => {
    setExpanded(initialExpanded)
  }, [initialExpanded])

  const headerInnerProps = {}

  if (!title && !textHTML) {
    return null
  }

  if (!timeout) {
    timeout = api.computedConfig.uiTransitionTimeout
  }

  if (isExpandable) {
    headerInnerProps['type'] = 'button'
    headerInnerProps['onClick'] = () => setExpanded((s) => !s)
    headerInnerProps['aria-expanded'] = expanded
    headerInnerProps['aria-controls'] = mainId
  }

  const slideTransitionProps = buildSlideTransitionProps({timeout})

  let classes = cn('info-toggle', className, {
    expandable: isExpandable,
    expanded,
    collapsed: !expanded,
  })

  return (
    <div className={classes}>
      {title && (
        <div className="header">
          <HeaderInnerTag className="header-inner" {...headerInnerProps}>
            <span className="icon">
              <IconInfo />
            </span>
            <HeaderInnerTitleTag className="title">{title}</HeaderInnerTitleTag>
          </HeaderInnerTag>
        </div>
      )}

      {textHTML && (
        <Transition
          in={expanded}
          mountOnEnter
          unmountOnExit
          {...slideTransitionProps}
        >
          <div className="main" id={mainId} role="region" aria-live="polite">
            <div className="main-inner">
              <div
                className="text"
                dangerouslySetInnerHTML={{__html: textHTML}}
              />
            </div>
          </div>
        </Transition>
      )}
    </div>
  )
})
